/**
 * Renders a pop-up dialog box
 */

import React from 'react';
import { observer } from 'mobx-react';

// Components
import Countdown from './Countdown';
import Button from './Button';

const Dialog = observer(
  class Dialog extends React.Component {
    constructor() {
      super();
  
      // Create refs
      this.dialog = React.createRef();
      this.description = React.createRef();

      // Handle focus
      this.focusableElements = [];
      this.firstItem = null;
      this.lastItem = null;
    }

    componentDidMount() {
      this.description.current.focus();
      this.focusableElements = this.dialog.current.querySelectorAll(
        'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])'
      );
      this.firstItem = this.focusableElements[0];
      if (this.focusableElements.length > 1) {
        this.lastItem = this.focusableElements[this.focusableElements.length - 1];
      }
    }

    focusNextElement = (e) => {
      if (e.target === this.firstItem) {
        this.lastItem.focus();
      }
      else {
        var index = Array.prototype.indexOf.call(this.focusableElements, e.target);
        this.focusableElements[index - 1].focus();
      }
    }
  
    focusPrevElement = (e) => {
      if (e.target === this.lastItem) {
        this.firstItem.focus();
      }
      else {
        var index = Array.prototype.indexOf.call(this.focusableElements, e.target);
        this.focusableElements[index + 1].focus();
      }
    }

    handleKeyDown = (e) => {
      const ESCAPE = 27;
      const TAB = 9;

      if (e.keyCode === TAB) {
        e.preventDefault();
        if (!this.lastItem || e.target === this.description.current) {
          this.firstItem.focus();
        } else {
          if (e.shiftKey) {
            this.focusPrevElement(e);
          } else {
            this.focusNextElement(e);
          }
        }
      }
      if (e.keyCode === ESCAPE) {
        this.handleCancel(e);
      }
    }

    handleCancel(e) {
      const { dialogCancelCallback } = this.props.store.AppStore;

      if (dialogCancelCallback) {
        dialogCancelCallback();
      }

      this.closeDialog(e);
    }

    closeDialog(e) {
      e.preventDefault();
      this.props.store.AppStore.closeDialog();
    }

    render() {
      const {
        dialogCallback,
        dialogContent,
        dialogCountdown,
        dialogOnExpire,
        dialogType
      } = this.props.store.AppStore;

      return (
        <div
          id="dialog"
          ref={this.dialog}
          className={`dialog ${dialogType === 'wide' && "dialog--wide"}`}
          role="dialog"
          aria-labelledby="dialog__heading"
          aria-modal="true"
          aria-describedby="dialog__description"
          onKeyDown={this.handleKeyDown}
        >
          <div className={`dialog__content ${dialogType === 'wide' && "dialog__content--wide"}`}>
            <div className="dialog__title">
              <h2 id="dialog__heading" className="dialog__heading">{dialogContent.heading}</h2>

              {dialogCountdown && (
                <Countdown
                  {...this.props}
                  onTimeout={dialogOnExpire}
                  timeout={dialogCountdown}
                />
              )}
            </div>

            {typeof dialogContent.body === 'string' || !dialogContent.body.html ?
              <p 
                id="dialog__description" 
                ref={this.description}
                tabIndex="-1"
              >{dialogContent.body}</p>
              : <section
                  id="dialog__description"
                  ref={this.description}
                  tabIndex="-1"
                  dangerouslySetInnerHTML={{ __html: dialogContent.body.content }}
                />
            }

            {dialogType !== 'confirm' && (
              <div className="dialog__action">
                <a
                  href="#app"
                  role="button"
                  className="btn" 
                  onKeyDown={e => {
                    const SPACE = 32;
                    if (e.keyCode === SPACE) {
                      this.closeDialog(e);
                    }
                  }}
                  onClick={e => this.closeDialog(e)}
                >
                  {dialogContent.button}
                </a>
              </div>
            )}

            {dialogType === 'confirm' && (
              <div className="dialog__action">
                <a
                  href="#app"
                  role="button"
                  className="btn btn--outline"
                  onKeyDown={e => {
                    const SPACE = 32;
                    if (e.keyCode === SPACE) {
                      this.handleCancel(e);
                    }
                  }}
                  onClick={(e) => this.handleCancel(e)}
                >
                  {dialogContent.cancel}
                </a>

                <Button
                  className="btn"
                  onClick={e => {
                    dialogCallback();
                    this.closeDialog(e);
                  }}
                  type="button"
                >
                  {dialogContent.button}
                </Button>
              </div>
            )}
          </div>
        </div>
      );
    }
  }
);

export default Dialog;
